import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetPurchaseOrderByIdMutation } from "../../../../api/services/client/clientApis";
import { NISProfessionalDetails } from "../../../../api/services/nisParticipants/models";
import { useGetNISParticipantDetailsById } from "../../../../api/services/nisParticipants/nisParticipantsApis";
import {
  useCompleteParticipantTransferMutation,
  useDeleteTransferActivity,
  useProgramActivateMutation,
  useSaveParticipantTabDetailsMutation,
  useSaveProgramFeeDetailsMutation,
  useUpdateParticipantTabDetailsMutation,
  useUpdateProgramFeeDetailsMutation,
  useUpdateProgramMutation,
} from "../../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  ITabs,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../components";
import {
  BE_ERROR_CODES,
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_PERCENTAGE,
  MODULE_PERMISSIONS,
  PAGE_URLS,
  ROLE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  STATUS_UNSPECIFIED_SITECORE_KEY,
  TIMEOUT_100,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  convertToUserFormat,
  filterEmptyParams,
  getFullName,
  getParsedErrorCode,
  getParsedErrorMessage,
  getValueOrBlank,
  isNotEmpty,
  scrollToTopContainer,
  trimParams,
} from "../../../../helpers/utils";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useToast } from "../../../../hooks/useToast";
import { navigationDataSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../redux/slices/program/programSelector";
import { updateDeliverableCountry } from "../../../../redux/slices/program/programSlice";
import { clearStartChildProgramNavigation } from "../../../../redux/slices/project/projectSlice";
import { CancelFieldModal } from "../../../createClient/components/activitiesAndDeliverables/components/cancelFieldModal/CancelFieldModal";
import { isHRContactOther } from "../../../viewNISParticipant/helper";
import {
  getBaseParticipantFormData,
  getParticipantFormForEdit,
  getProgramFeePayload,
  getProgramFormValue,
  getTabIds,
  getTabIndexes,
  getTabPanels,
  isDeliveryCountryJapanNetherlands,
  isValidPostalCode,
} from "../../helper";
import {
  ClientManaged,
  createProgramParticipantTabDefaultValue,
  createProgramProgramFeeTabDefaultValue,
  createProgramProgramTabDefaultValue,
  EMAIL_TYPE_SITE_CORE_KEY,
  OutreachEligible,
  ParticipantTabFormKey,
  ParticipantTabFormType,
  PHONE_TYPE_SITE_CORE_KEY,
  ProgramDetail,
  ProgramFeeTabFormKey,
  ProgramFeeTabFormType,
  ProgramTabFormHookType,
  ProgramTabFormKey,
  ProgramTabFormType,
  SalesCreditTableData,
} from "../../modelsAndSchemas/Models";
import {
  ParticipantTabSchema,
  ProgramFeeTabSchema,
  ProgramTabSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import {
  activityTimelineSiteCoreKeys,
  feesAndExpensesSiteCoreKeys,
  programDocumentsTabSiteCoreKeys,
  programSiteCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { CreateProgramFooter } from "../createProgramFooter/CreateProgramFooter";
import { SuccessFieldModal } from "../successFieldModal/SuccessFieldModal";
import { CreateProgramTabsWrapper } from "./CreateProgramTabs.style";
import { DocumentsTab } from "./tabs/documents/DocumentsTab";
import { FeesAndExpenseMain } from "./tabs/feesAndExpense/FeesAndExpenseMain";
import { ParticipantTab } from "./tabs/participant/ParticipantTab";
import { ProgramTab } from "./tabs/program/ProgramTab";
import { ProgramFeeTab } from "./tabs/programFee/ProgramFeeTab";

interface CreateProgramTabsProps {
  pageId: string;
  programDetail?: ProgramDetail;
  handleNavigateBack: (shouldShowConfirmation?: boolean) => void;
  programId: string;
  isEditProgram?: boolean;
  editProgramData?: ProgramTabFormType;
  tabValue?: number;
  isFromTransfer?: boolean;
  onRefreshProgramDetail?: (id: string) => void;
  isFromEditProjectChildPrograms?: boolean;
  handleDirtyTabCallBack?: (isDirty: boolean) => void;
}

export const CreateProgramTabs: FC<CreateProgramTabsProps> = ({
  pageId,
  programDetail,
  handleNavigateBack,
  programId,
  isEditProgram = false,
  editProgramData,
  tabValue = 0, // default tab loaded as first tab
  isFromTransfer = false,
  onRefreshProgramDetail,
  isFromEditProjectChildPrograms,
  handleDirtyTabCallBack,
}) => {
  const dispatch = useDispatch();
  const navigationData = useSelector(navigationDataSelector);
  const programStatus = useSelector(programStatusSelector);
  const [selectedTab, setSelectedTab] = useState(tabValue);
  const nextTabIndexRef = useRef(tabValue);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showScheduleTotalAmountWarning, setShowScheduleTotalAmountWarning] =
    useState<{
      isOpen: boolean;
      isPercentage: boolean;
    }>({
      isOpen: false,
      isPercentage: false,
    });
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState(false);
  const [refetchProgramFee, setRefetchProgramFee] = useState(false);
  const [showPOCurrencyNotMatchModal, setShowPOCurrencyNotMatchModal] =
    useState(false);
  const [showSalesCreditMinimumModal, setShowSalesCreditMinimumModal] =
    useState(false);
  const [showSalesCreditPercentageModal, setShowSalesCreditPercentageModal] =
    useState(false);
  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);
  const [isMustStartDateDirty, setIsMustStartDateDirty] = useState(false);
  const [onParentSubmitPressed, setOnParentSubmitPressed] = useState(false);

  const navigate = useNavigate();
  const {
    fetchLabel,
    commonValidationMessages,
    commonLabels,
    fetchCommonLabel,
    fetchComponentTitle,
    commonBillingConfigurationDetails,
    fetchValidationMessage,
  } = useSiteCore();

  const { hasModulePermission } = usePermissions();

  const { displayToast } = useToast();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const location = useLocation();
  const isFromNIS = location.state?.isFromNISParticipant ?? false;
  const nisParticipantId = location.state?.nisParticipantData?.id;

  const TAB_INDEX = getTabIndexes(isEditProgram);
  const TAB_IDS = getTabIds(pageId, isEditProgram);
  const TAB_PANELS = getTabPanels(pageId, isEditProgram);

  const {
    data: nisParticipantData,
    isFetching: isNISParticipantDataLoading,
    isError: isNISParticipantDataError,
  } = useGetNISParticipantDetailsById(nisParticipantId);

  const {
    mutateAsync: updateProgramApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateProgramMutation();

  const {
    mutateAsync: activateProgramAPi,
    isPending: isActivateProgramPending,
    isError: isActivateProgramError,
    isSuccess: isActivateProgramSuccess,
  } = useProgramActivateMutation();

  const {
    mutateAsync: saveParticipantApi,
    data: saveParticipantId,
    isPending: isSaveParticipantPending,
    isError: isSaveParticipantError,
    isSuccess: isSaveParticipantSuccess,
    error: saveParticipantError,
  } = useSaveParticipantTabDetailsMutation();

  const {
    mutateAsync: updateParticipantApi,
    isPending: isUpdateParticipantPending,
    isError: isUpdateParticipantError,
    isSuccess: isUpdateParticipantSuccess,
    error: updateParticipantError,
  } = useUpdateParticipantTabDetailsMutation();

  const {
    mutateAsync: saveProgramFeeDetailsApi,
    isPending: isSaveProgramFeeDetailsPending,
    isError: isSaveProgramFeeDetailsError,
    isSuccess: isSaveProgramFeeDetailsSuccess,
    error: saveProgramFeeDetailsError,
  } = useSaveProgramFeeDetailsMutation();

  const {
    mutateAsync: updateProgramFeeDetailsApi,
    isPending: isUpdateProgramFeeDetailsPending,
    isError: isUpdateProgramFeeDetailsError,
    isSuccess: isUpdateProgramFeeDetailsSuccess,
    error: updateProgramFeeDetailsError,
  } = useUpdateProgramFeeDetailsMutation();
  const {
    mutateAsync: deleteTransferActivity,
    isPending: isDeleting,
    isError: isDeleteError,
    isSuccess: isDeleteSuccess,
    error: deleteError,
  } = useDeleteTransferActivity();

  const {
    mutateAsync: completeParticipantTransfer,
    isPending: isCompleteParticipantTransferPending,
    isError: isCompleteParticipantTransferError,
    isSuccess: isCompleteParticipantTransferSuccess,
    error: completeParticipantTransferError,
  } = useCompleteParticipantTransferMutation();

  const {
    mutateAsync: getClientRelationshipOwners,
    isPending: clientRelationshipOwnersPending,
    data: clientRelationshipOwnerList,
  } = useGetUsersDetailByRoleMutation();

  const { mutateAsync: getPurchaseOrderById, data: purchaseOrderData } =
    useGetPurchaseOrderByIdMutation();

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.keyProgramDetails, key);
  };
  const getModalLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.transferProcessPopup, key);
  };

  const isEditParticipant = useMemo(() => {
    return isEditProgram || Boolean(saveParticipantId);
  }, [isEditProgram, saveParticipantId]);

  const tabIdProgram = TAB_IDS.PROGRAM;
  const tabIdParticipant = TAB_IDS.PARTICIPANT;
  const tabIdDocuments = TAB_IDS.DOCUMENTS;
  const tabIdProgramFee = TAB_IDS.PROGRAM_FEE;
  const tabIdFeesAndExpenses = TAB_IDS.FEES_AND_EXPENSES;

  const tabs: ITabs[] = [
    {
      id: tabIdProgram,
      label: getLabel(programSiteCoreKeys.program),
    },
    {
      id: tabIdParticipant,
      label: getLabel(programSiteCoreKeys.participant),
    },
    ...(isEditProgram
      ? [
          {
            id: tabIdDocuments,
            label: fetchComponentTitle(
              programDocumentsTabSiteCoreKeys.documentsTitle,
            ),
          },
        ]
      : []),
    {
      id: tabIdProgramFee,
      label: fetchComponentTitle(programSiteCoreKeys.programFeeTitle),
    },
    // Conditionally remove the Fees and Expenses tab
    ...(isFromEditProjectChildPrograms
      ? []
      : [
          {
            id: tabIdFeesAndExpenses,
            label: fetchComponentTitle(
              feesAndExpensesSiteCoreKeys.feesAndExpenses,
            ),
          },
        ]),
  ];

  const getErrorCodeAndShowToast = () => {
    const errorObj = isSaveParticipantError
      ? saveParticipantError
      : updateParticipantError;

    const errorCode = getParsedErrorCode(errorObj);

    if (errorCode === BE_ERROR_CODES.NEXT_CONTACT_DATE) {
      setParticipantError(ParticipantTabFormKey.nextContactDate, {
        type: "custom",
        message: fetchLabel(
          activityTimelineSiteCoreKeys.activityTimeline,
          activityTimelineSiteCoreKeys.nextContactDateMustBeTodayOrAFutureDate,
        ),
      });
    } else if (errorCode === BE_ERROR_CODES.INVALID_POSTAL_CODE) {
      const invalidPostalCodeMessage = fetchLabel(
        programSiteCoreKeys.addressDetails,
        programSiteCoreKeys.postalCodeFormatErrorMessage,
      );
      displayToast(invalidPostalCodeMessage, "error");
    } else {
      getCommonErrorMessageAndShowToast(errorObj);
    }
  };

  const getApiErrorByStatus = () => {
    if (isDeleteError) {
      return deleteError;
    } else if (isUpdateProgramFeeDetailsError) {
      return updateProgramFeeDetailsError;
    } else if (isSaveProgramFeeDetailsError) {
      return saveProgramFeeDetailsError;
    } else if (isCompleteParticipantTransferError) {
      return completeParticipantTransferError;
    } else if (isParticipantTabDirty) {
      return saveParticipantError;
    }
  };

  const getCommonErrorMessageAndShowToast = (error: any) => {
    if (error) {
      const parsedErrorMessage = getParsedErrorMessage(error);
      displayToast(parsedErrorMessage, "error");
    }
  };

  useEffect(() => {
    if (isUpdateError || isActivateProgramError || isNISParticipantDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    } else if (isSaveParticipantError || isUpdateParticipantError) {
      getErrorCodeAndShowToast();
    } else {
      const error = getApiErrorByStatus();
      getCommonErrorMessageAndShowToast(error);
    }
  }, [
    isUpdateError,
    isActivateProgramError,
    isSaveParticipantError,
    isUpdateParticipantError,
    isDeleteError,
    isSaveProgramFeeDetailsError,
    isUpdateProgramFeeDetailsError,
    isCompleteParticipantTransferError,
  ]);

  useEffect(() => {
    getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
  }, []);

  const isLoading =
    clientRelationshipOwnersPending ||
    isSaveParticipantPending ||
    isUpdatePending ||
    isNISParticipantDataLoading ||
    isActivateProgramPending ||
    isUpdateParticipantPending ||
    isDeleting ||
    isSaveProgramFeeDetailsPending ||
    isUpdateProgramFeeDetailsPending ||
    isCompleteParticipantTransferPending;

  useEffect(() => {
    if (isLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  const getProgramSuccessMessage = () => {
    return isEditProgram
      ? getLabel(programSiteCoreKeys.programUpdatedSuccessMessage)
      : getLabel(programSiteCoreKeys.saveProgramSuccessText);
  };

  const getSuccessMessage = {
    update: () => {
      return fetchLabel(
        programSiteCoreKeys.participantInformation,
        programSiteCoreKeys.candidateUpdateSuccessText,
      );
    },
    save: () => {
      return fetchLabel(
        programSiteCoreKeys.participantInformation,
        programSiteCoreKeys.candidateSaveSuccessText,
      );
    },
  };

  const getParticipantSuccessMessage = () => {
    return getSuccessMessage[isEditProgram ? "update" : "save"]();
  };

  const getProgramFeeDetailsSuccessText = {
    update: () => {
      return fetchLabel(
        programSiteCoreKeys.programFeeTitle,
        programSiteCoreKeys.programUpdatedMessage,
      );
    },
    save: () => {
      return fetchLabel(
        programSiteCoreKeys.participantInformation,
        programSiteCoreKeys.candidateSaveSuccessText,
      );
    },
  };

  const getProgramFeeDetailsSuccessMessage = (isUpdate: boolean) => {
    return getProgramFeeDetailsSuccessText[isUpdate ? "update" : "save"]();
  };

  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: getProgramSuccessMessage(),
        }),
      );
      programReset(programGetValues());
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.PARTICIPANT,
      );
      scrollToTopContainer(500);
    }
  }, [isUpdateSuccess]);

  const navigateToDocumentsTab = () => {
    if (isEditProgram) {
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.DOCUMENTS,
      );
    } else {
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.PROGRAM_FEE,
      );
    }
    scrollToTopContainer(500);
  };

  useEffect(() => {
    if (isSaveParticipantSuccess || isUpdateParticipantSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: getParticipantSuccessMessage(),
        }),
      );
      isUpdateParticipantSuccess && resetParticipant(participantGetValues());
      navigateToDocumentsTab();
    }
  }, [isSaveParticipantSuccess, isUpdateParticipantSuccess]);

  useEffect(() => {
    if (selectedTab === TAB_INDEX.PROGRAM_FEE) {
      setRefetchProgramFee(!refetchProgramFee);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (isSaveProgramFeeDetailsSuccess) {
      setRefetchProgramFee(!refetchProgramFee);
    }
    if (
      (isSaveProgramFeeDetailsSuccess || isUpdateProgramFeeDetailsSuccess) &&
      programId
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: getProgramFeeDetailsSuccessMessage(
            isUpdateProgramFeeDetailsSuccess,
          ),
        }),
      );
      handleProgramFeeTabNavigation();
    }
  }, [isSaveProgramFeeDetailsSuccess, isUpdateProgramFeeDetailsSuccess]);

  useEffect(() => {
    if (isCompleteParticipantTransferSuccess && isFromTransfer) {
      setShowSuccessModal(true);
    }
  }, [isCompleteParticipantTransferSuccess]);

  const handleProgramFeeTabNavigation = () => {
    if (isFromEditProjectChildPrograms) {
      isUpdateProgramFeeDetailsSuccess &&
        programFeeReset(programFeeGetValues());
      navigate(-1);
    } else {
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.FEES_AND_EXPENSES,
      );
      isProgramFeeTabDirty && scrollToTopContainer(500);
    }
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    // Prevent a keyboard/click-initiated tab switch from overwriting the
    // destination of a save that is still in flight.
    if (isLoading) {
      return;
    }
    if (value < selectedTab || selectedTab === TAB_INDEX.FEES_AND_EXPENSES) {
      nextTabIndexRef.current = value;
      setSelectedTab(value);
    } else {
      handleContinueButtonClick(value);
    }
  };

  const methodsProgram = useForm<ProgramTabFormHookType>({
    mode: "all",
    defaultValues: createProgramProgramTabDefaultValue,
    resolver: yupResolver(
      ProgramTabSchema(commonValidationMessages, fetchValidationMessage),
    ),
  });

  const {
    reset: programReset,
    handleSubmit: programTabSubmit,
    getValues: programGetValues,
    setValue: programSetValue,
    watch: programWatch,
    formState: { isDirty: isProgramTabDirty },
  } = methodsProgram;

  useEffect(() => {
    const { poNumber } = programGetValues();
    if (poNumber) {
      getPurchaseOrderById(poNumber);
    }
  }, [programWatch(ProgramTabFormKey.poNumber)]);

  const setProgramPrefilledWithBillingConfigurationValues = () => {
    return {
      [ProgramTabFormKey.poNumber]: getValueOrBlank(
        programDetail?.poNumber?.id,
      ),
      [ProgramTabFormKey.billingContact]: getValueOrBlank(
        programDetail?.billingContact?.id,
      ),
      [ProgramTabFormKey.hrContact]: getValueOrBlank(
        programDetail?.hrContact?.id,
      ),
      [ProgramTabFormKey.projectManager]: getValueOrBlank(
        programDetail?.projectManagerId,
      ),
      [ProgramTabFormKey.clientInformation1]: getValueOrBlank(
        programDetail?.clientInformation1,
      ),
      [ProgramTabFormKey.clientInformation2]: getValueOrBlank(
        programDetail?.clientInformation2,
      ),
      [ProgramTabFormKey.clientInformation3]: getValueOrBlank(
        programDetail?.clientInformation3,
      ),
      [ProgramTabFormKey.site]: getValueOrBlank(programDetail?.site),
    };
  };

  const getDefaultSalesCredit = useMemo((): SalesCreditTableData[] => {
    const localClientRelationShipOwnerId =
      programDetail?.localClientRelationShipOwnerId;
    const selectedClientRelationshipOwner = clientRelationshipOwnerList?.find(
      ({ id }) => id === localClientRelationShipOwnerId,
    );

    if (selectedClientRelationshipOwner) {
      const { firstName, lastName, email, officeName } =
        selectedClientRelationshipOwner;

      return [
        {
          localClientRelationshipOwnerId: localClientRelationShipOwnerId,
          firstName,
          lastName,
          email: getValueOrBlank(email),
          percentage: "100",
          officeName: getValueOrBlank(officeName),
        },
      ];
    }
    return [] as SalesCreditTableData[];
  }, [
    clientRelationshipOwnerList,
    programDetail?.localClientRelationShipOwnerId,
  ]);

  const setProgramPrefilledValues = () => {
    return {
      [ProgramTabFormKey.solutionOffering]: getValueOrBlank(
        programDetail?.solutionOffering,
      ),
      [ProgramTabFormKey.programClass]: getValueOrBlank(
        programDetail?.programClass,
      ),
      [ProgramTabFormKey.businessUnit]: getValueOrBlank(
        programDetail?.businessUnit,
      ),
      [ProgramTabFormKey.costCentre]: getValueOrBlank(
        programDetail?.costCenter,
      ),
      [ProgramTabFormKey.billingOffice]: getValueOrBlank(
        programDetail?.billingOffice,
      ),
      [ProgramTabFormKey.salesOffice]: getValueOrBlank(
        programDetail?.salesOffice,
      ),
      [ProgramTabFormKey.clientRelationShipOwner]: getValueOrBlank(
        programDetail?.localClientRelationShipOwnerId,
      ),

      ...(isFromTransfer ? { [ProgramTabFormKey.deliveryOffice]: "" } : {}),
    };
  };

  useEffect(() => {
    if (
      isNotEmpty(programDetail) &&
      clientRelationshipOwnerList &&
      !isEditProgram
    ) {
      programSetValue(
        ProgramTabFormKey.salesCreditDetails,
        getDefaultSalesCredit,
      );
    }
  }, [programDetail?.clientId, clientRelationshipOwnerList, isEditProgram]);

  useEffect(() => {
    if (isNotEmpty(programDetail)) {
      const programValues = {
        ...createProgramProgramTabDefaultValue,
        ...programGetValues(), // Preserve existing form values
        ...setProgramPrefilledValues(),
        salesCreditDetails: programGetValues().salesCreditDetails,
        ...(programDetail?.isBillingConfigurationExists
          ? { ...setProgramPrefilledWithBillingConfigurationValues() }
          : {}),
      };
      programReset(programValues);

      const participantValues = {
        ...createProgramParticipantTabDefaultValue,
        ...participantGetValues(), // Preserve existing form values
        [ParticipantTabFormKey.countryId]: getValueOrBlank(
          programDetail?.deliveryCountry,
        ),
        [ParticipantTabFormKey.countryName]: getValueOrBlank(
          programDetail?.deliveryCountryName,
        ),
        [ParticipantTabFormKey.clientName]: getValueOrBlank(
          programDetail?.clientName,
        ),
        // if CMC the client managed as default value at create program
        [ParticipantTabFormKey.eligibility]:
          isCMCProgram || isTMAssessmentProgram || isTMLeaderCoachingProgram
            ? ClientManaged
            : statusUnspecified(),
      };
      if (!isEditProgram && !isFromNIS) resetParticipant(participantValues);
    }
  }, [programDetail?.clientId]);

  useEffect(() => {
    if (isNotEmpty(programDetail)) {
      programSetValue(
        ProgramTabFormKey.solutionOffering,
        getValueOrBlank(programDetail?.solutionOffering),
      );
      programSetValue(
        ProgramTabFormKey.programClass,
        getValueOrBlank(programDetail?.programClass),
      );
    }
  }, [programDetail]);

  /* "Set the deliverable country in Redux because the delivery 
  country needs to be passed in order to fetch the coach list
    during the participant-to-coach assignment process."*/
  useEffect(() => {
    if (programDetail?.deliveryCountry) {
      dispatch(updateDeliverableCountry(programDetail.deliveryCountry));
    }
  }, [programDetail?.deliveryCountry]);

  const checkSalesCreditValidation = () => {
    const { salesCreditDetails } = programGetValues();
    let isSalesCreditValid = true;
    if ((salesCreditDetails?.length ?? 0) <= 0) {
      setShowSalesCreditMinimumModal(true);
      isSalesCreditValid = false;
    } else {
      const totalPercentage = (salesCreditDetails ?? []).reduce(
        (sum: number, item: { percentage?: string }) =>
          sum + (Number(item.percentage) || 0),
        0,
      );
      if (totalPercentage !== MAX_PERCENTAGE) {
        setShowSalesCreditPercentageModal(true);
        isSalesCreditValid = false;
      }
    }
    return isSalesCreditValid;
  };

  const isCMCProgram = useMemo(() => {
    return (
      programDetail?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programDetail?.solutionOfferingSiteCoreKey]);

  const isTMAssessmentProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programDetail?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_LEADER_COACHING;
  }, [programDetail?.tmProgramType]);

  const hasBusinessApprovalField = useMemo(() => {
    return isDeliveryCountryJapanNetherlands(programDetail?.deliveryCountry);
  }, [programDetail?.deliveryCountry]);

  const isStatusUnspecified = useMemo(() => {
    return (
      programDetail?.outreachEligibilityKeySetting ===
      STATUS_UNSPECIFIED_SITECORE_KEY
    );
  }, [programDetail?.outreachEligibilityKeySetting]);

  const statusUnspecified = () =>
    isStatusUnspecified
      ? ""
      : (programDetail?.outreachEligibilityKeySetting ?? OutreachEligible);

  const callProgramDetailsApi = () => {
    if (!checkSalesCreditValidation()) return;

    const { prefix, firstName, lastName } = programGetValues();

    const participantValues = {
      ...participantGetValues(),
      [ParticipantTabFormKey.prefix]: getValueOrBlank(prefix),
      [ParticipantTabFormKey.firstName]: firstName,
      [ParticipantTabFormKey.lastName]: lastName,
      [ParticipantTabFormKey.clientName]: getValueOrBlank(
        programDetail?.clientName,
      ),
    };
    // setting default values for participant in case of create program
    if (!isEditProgram) resetParticipant(participantValues);

    if (isProgramTabDirty) {
      updateProgramApi(
        getProgramFormValue(
          programGetValues,
          programId,
          programDetail,
          isCMCProgram,
          isTMAssessmentProgram,
          isTMLeaderCoachingProgram,
          hasBusinessApprovalField,
        ),
      );
    } else {
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.PARTICIPANT,
      );
      scrollToTopContainer(500);
    }
  };

  //reset the data based on edit program
  useEffect(() => {
    if (
      isNotEmpty(editProgramData) &&
      isEditProgram &&
      isNotEmpty(programDetail)
    ) {
      const programPrefilledValues = setProgramPrefilledValues();
      const programConstCenterFromClient =
        programPrefilledValues.costCentre ?? "";
      programReset({
        ...editProgramData,
        ...programPrefilledValues,
        [ProgramTabFormKey.costCentre]: isNotEmpty(editProgramData?.costCentre)
          ? editProgramData?.costCentre
          : programConstCenterFromClient,
        [ProgramTabFormKey.firstName]: editProgramData?.firstName,
        [ProgramTabFormKey.lastName]: editProgramData?.lastName,
        [ProgramTabFormKey.prefix]: getValueOrBlank(editProgramData?.prefix),
        [ProgramTabFormKey.deliveryOffice]: getValueOrBlank(
          editProgramData?.deliveryOffice,
        ),
      });
    }
  }, [editProgramData, programDetail, programStatus]);

  const getHRContactValue = (professionalDetails?: NISProfessionalDetails) => {
    if (professionalDetails && !isHRContactOther(professionalDetails)) {
      const hrContactId = getValueOrBlank(professionalDetails?.hrContact?.id);
      if (isNotEmpty(hrContactId)) return hrContactId;
    }
    return getValueOrBlank(programDetail?.hrContact?.id);
  };

  const getProjectManagerValue = (
    professionalDetails?: NISProfessionalDetails,
  ) => {
    if (professionalDetails) {
      const projectManagerId = getValueOrBlank(
        professionalDetails?.projectManager?.id,
      );
      if (isNotEmpty(projectManagerId)) return projectManagerId;
    }
    return getValueOrBlank(programDetail?.projectManagerId);
  };

  useEffect(() => {
    if (isFromNIS && isNotEmpty(nisParticipantData)) {
      const { profile, professionalDetails, address } = nisParticipantData;
      programReset({
        ...programGetValues(), // Preserve existing form values
        [ProgramTabFormKey.programName]: getFullName(
          profile?.firstName,
          profile?.lastName,
          true,
        ),
        [ProgramTabFormKey.firstName]: getValueOrBlank(profile?.firstName),
        [ProgramTabFormKey.lastName]: getValueOrBlank(profile?.lastName),
        [ProgramTabFormKey.projectManager]:
          getProjectManagerValue(professionalDetails),
        [ProgramTabFormKey.hrContact]: getHRContactValue(professionalDetails),
      });

      resetParticipant({
        ...participantGetValues(), // Preserve existing form values
        [ParticipantTabFormKey.clientName]: getValueOrBlank(
          professionalDetails?.employer?.name,
        ),
        [ParticipantTabFormKey.nisId]: getValueOrBlank(profile?.nisId),
        [ParticipantTabFormKey.jobTitle]: getValueOrBlank(profile?.jobTitle),
        [ParticipantTabFormKey.employeeId]: getValueOrBlank(
          professionalDetails?.employeeId,
        ),
        [ParticipantTabFormKey.releaseDate]: isNotEmpty(
          professionalDetails?.releaseDate,
        )
          ? convertToUserFormat(
              professionalDetails?.releaseDate,
              userCountryDateFormat,
            )
          : "",
        [ParticipantTabFormKey.primaryEmail]: getValueOrBlank(profile?.email),
        [ParticipantTabFormKey.emailType1]:
          EMAIL_TYPE_SITE_CORE_KEY.personalEmail,
        [ParticipantTabFormKey.alternativeEmail]: getValueOrBlank(
          profile?.workEmail,
        ),
        [ParticipantTabFormKey.emailType2]: isNotEmpty(profile?.workEmail)
          ? EMAIL_TYPE_SITE_CORE_KEY.workEmail
          : "",
        [ParticipantTabFormKey.primaryNumber]: getValueOrBlank(
          profile?.mobileNumber?.number,
        ),
        [ParticipantTabFormKey.primaryCountryCode]: getValueOrBlank(
          profile?.mobileNumber?.countryCode,
        ),
        [ParticipantTabFormKey.primaryIsoCode]: getValueOrBlank(
          profile?.mobileNumber?.isoCode,
        ),
        [ParticipantTabFormKey.concatType1]: isNotEmpty(
          profile?.mobileNumber?.number,
        )
          ? PHONE_TYPE_SITE_CORE_KEY.mobile
          : "",

        [ParticipantTabFormKey.postalCode]: getValueOrBlank(
          address?.postalCode,
        ),
        [ParticipantTabFormKey.address1]: getValueOrBlank(address?.address1),
        [ParticipantTabFormKey.address2]: getValueOrBlank(address?.address2),
        [ParticipantTabFormKey.address3]: getValueOrBlank(address?.address3),
        [ParticipantTabFormKey.countryId]: getValueOrBlank(
          address?.country?.countryId,
        ),
        [ParticipantTabFormKey.countryName]: getValueOrBlank(
          address?.country?.countryName,
        ),
        [ParticipantTabFormKey.city]: getValueOrBlank(address?.city),
        [ParticipantTabFormKey.stateProvince]: getValueOrBlank(
          address?.stateId,
        ),
        [ParticipantTabFormKey.participantReadyToStartJobSearch]:
          programSiteCoreKeys.participantStartJobSearchNo,
        // if CMC the client managed as default value at create program
        [ParticipantTabFormKey.eligibility]:
          isCMCProgram || isTMAssessmentProgram || isTMLeaderCoachingProgram
            ? ClientManaged
            : statusUnspecified(),
      });
    }
  }, [isFromNIS, nisParticipantData, programDetail]);

  useEffect(() => {
    if (isDeleteSuccess) {
      navigate(`${PAGE_URLS.DASHBOARD}`);
    }
  }, [isDeleteSuccess]);

  const hasPermissionOutreach = hasModulePermission(
    MODULE_PERMISSIONS.EDIT_OUTREACH,
  );

  const methodsParticipant = useForm<ParticipantTabFormType>({
    mode: "all",
    defaultValues: createProgramParticipantTabDefaultValue,
    resolver: yupResolver(
      ParticipantTabSchema(
        {
          ...commonValidationMessages,
          notificationDateErrorMessage: fetchLabel(
            programSiteCoreKeys.outreach,
            programSiteCoreKeys.notificationDateOutreachEligibleDateValidationMessage,
          ),
          invalidEmployeeIdMessage: fetchLabel(
            programSiteCoreKeys.sponsoringEmployer,
            programSiteCoreKeys.employeeIDValidationMessage,
          ),
          alreadyExistsEmailMessage: fetchLabel(
            programSiteCoreKeys.contactAndNotificationPreferences,
            programSiteCoreKeys.emailIDAlreadyExistsMessage,
          ),
          alreadyExistsNumberMessage: fetchLabel(
            programSiteCoreKeys.contactAndNotificationPreferences,
            programSiteCoreKeys.phoneNumberAlreadyExistsMessage,
          ),
          unsubscribeValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.unsubscribedDateValidationMessage,
          ),
          subscribeValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.programEndDateValidationMessage,
          ),
          personalIdValidationMessage: fetchLabel(
            programSiteCoreKeys.participantInformation,
            programSiteCoreKeys.personalIdValidationMessage,
          ),
        },
        getValueOrBlank(userCountryDateFormat),
        isCMCProgram,
        !!programDetail?.isEmployeeIdRequired,
        !!programDetail?.isEmployeeBandRequired,
        getValueOrBlank(programDetail?.deliveryCountry),
        isTMAssessmentProgram,
        isTMLeaderCoachingProgram,
        fetchValidationMessage,
        isEditProgram,
        hasPermissionOutreach,
        isMustStartDateDirty,
      ),
    ),
  });

  const {
    reset: resetParticipant,
    handleSubmit: participantTabSubmit,
    getValues: participantGetValues,
    formState: {
      isDirty: isParticipantTabDirty,
      touchedFields,
      dirtyFields: participantDirtyFields,
      errors: participantErrors,
    },
    setError: setParticipantError,
    trigger,
  } = methodsParticipant;

  useEffect(() => {
    const isMustStartByDateDirty =
      participantDirtyFields.mustStartByDate ?? false;
    setIsMustStartDateDirty(isMustStartByDateDirty);
  }, [participantDirtyFields.mustStartByDate]);

  // Re-trigger validation after state updates (when field is touched in edit mode)
  useEffect(() => {
    if (isEditProgram && isMustStartDateDirty) {
      trigger(ParticipantTabFormKey.mustStartByDate);
    }
  }, [isMustStartDateDirty, isEditProgram, trigger]);

  const earlyPayPercentageErrorMessage = fetchLabel(
    programSiteCoreKeys.programFeeTitle,
    programSiteCoreKeys.earlyPayPercentageToolTipMessage,
  );

  const minimumTransferFeeShouldBeLessThanProgramFee = fetchLabel(
    programSiteCoreKeys.programFeeTitle,
    programSiteCoreKeys.minimumTransferFeeShouldBeLessThanProgramFee,
  );

  const programFeeShouldBeGreaterThanMinimumTransferFee = fetchLabel(
    programSiteCoreKeys.programFeeTitle,
    programSiteCoreKeys.programFeeShouldBeGreaterThanMinimumTransferFee,
  );

  const methodsProgramFee = useForm<ProgramFeeTabFormType>({
    mode: "all",
    defaultValues: createProgramProgramFeeTabDefaultValue,
    resolver: yupResolver(
      ProgramFeeTabSchema(
        commonValidationMessages,
        earlyPayPercentageErrorMessage,
        minimumTransferFeeShouldBeLessThanProgramFee,
        programFeeShouldBeGreaterThanMinimumTransferFee,
      ),
    ),
  });

  const {
    reset: programFeeReset,
    handleSubmit: programFeeTabSubmit,
    getValues: programFeeGetValues,
    formState: {
      defaultValues: programFeeDefaultValues,
      isDirty: isProgramFeeTabDirty,
      dirtyFields,
      touchedFields: programFeeTouchedFields,
    },
  } = methodsProgramFee;

  // temp added code to check the dirty fields and values of program fee tab, will remove once we are sure about the fields which are causing the issue of showing the update confirmation popup even when there is no change in values on program fee tab
  useEffect(() => {
    console.log("azhar programFeeTouchedFields ", programFeeTouchedFields);
    console.log("azhar programFeeDefaultValues ", programFeeDefaultValues);
    console.log("azhar programFeeGetValues ", programFeeGetValues());
    console.log("azhar dirtyFields ", dirtyFields);
    console.log("azhar isProgramFeeTabDirty ", isProgramFeeTabDirty);
  }, [
    isProgramFeeTabDirty,
    dirtyFields,
    programFeeTouchedFields,
    programFeeDefaultValues,
  ]);

  const handleProgramFeeRelatedStuff = () => {
    programFeeTabSubmit(validateAndCallProgramFeeApi)();
  };

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);

  const isFieldDirty =
    isProgramTabDirty || isProgramFeeTabDirty || isParticipantTabDirty;

  useEffect(() => {
    dispatch(setIsDataModifiedCurrentPage(isFieldDirty));
  }, [isFieldDirty]);

  const handleBackNavigation = () => {
    handleNavigateBack(
      isFromEditProjectChildPrograms &&
        (isProgramTabDirty || isParticipantTabDirty || isProgramFeeTabDirty),
    );
  };

  useEffect(() => {
    handleDirtyTabCallBack?.(
      isProgramTabDirty || isParticipantTabDirty || isProgramFeeTabDirty,
    );
  }, [
    isFromEditProjectChildPrograms,
    isProgramTabDirty,
    isParticipantTabDirty,
    isProgramFeeTabDirty,
  ]);

  const handleParticipantTabAPI = () => {
    if (
      !isValidPostalCode(
        participantErrors,
        setParticipantError,
        ParticipantTabFormKey.postalCode,
      )
    ) {
      trigger();
      return;
    }

    participantTabSubmit(callParticipantApi)();
  };

  const handleProgramFeeTabContinue = () => {
    if (isEditProgram && isProgramFeeTabDirty) {
      setShowUpdateConfirmationModal(true);
    } else {
      handleProgramFeeRelatedStuff();
    }
  };

  const handleFeesAndExpensesTabContinue = () => {
    if (isFromTransfer) {
      setShowTransferModal(true);
      return;
    }

    if (!isEditProgram) {
      // in case of create program calling activate API on last tab submit
      activateProgramAPi({
        programId,
        nisParticipantId: isFromNIS ? nisParticipantId : undefined,
      });
    } else {
      navigate(`${PAGE_URLS.VIEW_PROGRAM}/${programId}`);
    }
  };

  const tabHandlers = {
    [TAB_INDEX.PROGRAM]: () => programTabSubmit(callProgramDetailsApi)(),
    [TAB_INDEX.PARTICIPANT]: handleParticipantTabAPI,
    [TAB_INDEX.DOCUMENTS]: () => {
      setSelectedTab(
        isEditProgram ? nextTabIndexRef.current : TAB_INDEX.PROGRAM_FEE,
      );
    },
    [TAB_INDEX.PROGRAM_FEE]: handleProgramFeeTabContinue,
    [TAB_INDEX.FEES_AND_EXPENSES]: handleFeesAndExpensesTabContinue,
  };

  const handleContinueButtonClick = (nextIndex: number) => {
    if (isLoading) {
      return;
    }
    nextTabIndexRef.current = nextIndex;
    const handler = tabHandlers[selectedTab];
    if (handler) {
      handler();
    }
  };

  useEffect(() => {
    if (isActivateProgramSuccess) {
      navigate(`${PAGE_URLS.VIEW_PROGRAM}/${programId}`);
    }
  }, [isActivateProgramSuccess]);

  const watchProgramFeeBillingCurrency = useWatch({
    control: methodsProgramFee.control,
    name: ProgramFeeTabFormKey.currencyId,
    defaultValue: "",
  });

  const submitStrategies = {
    create: () => {
      const baseData = getBaseParticipantFormData(
        participantGetValues,
        touchedFields.countryName,
        userCountryDateFormat,
        isCMCProgram,
        isTMLeaderCoachingProgram,
      );
      saveParticipantApi({
        params: trimParams(filterEmptyParams(baseData)),
        programId,
      });
    },
    update: () => {
      const baseData = getBaseParticipantFormData(
        participantGetValues,
        touchedFields.countryName,
        userCountryDateFormat,
        isCMCProgram,
        isTMLeaderCoachingProgram,
      );
      updateParticipantApi({
        params: trimParams(
          filterEmptyParams(
            getParticipantFormForEdit(
              participantGetValues,
              baseData,
              userCountryDateFormat,
            ),
          ),
        ),
        programId,
      });
    },
  };

  const handleOkButton = () => {
    navigate(`${PAGE_URLS.VIEW_PROGRAM}/${programId}`);
  };
  const callParticipantApi = () => {
    setOnParentSubmitPressed(!onParentSubmitPressed);
    if (isParticipantTabDirty) {
      const submitAction = isEditParticipant
        ? submitStrategies.update
        : submitStrategies.create;
      submitAction();
    } else {
      navigateToDocumentsTab();
    }
  };

  const validateAndCallProgramFeeApi = () => {
    const { billingSchedules, billingTypeId, feePaymentType, fee } =
      programFeeGetValues();
    if (
      [
        BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
        BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
      ].includes(billingTypeId)
    ) {
      let totalAmountOrPercentage = 0;
      const isPercentage =
        feePaymentType === BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage;
      billingSchedules?.forEach((item) => {
        totalAmountOrPercentage += isPercentage
          ? Number(item.percentage)
          : Number(item?.amount);
      });
      if (
        (isPercentage && totalAmountOrPercentage === MAX_PERCENTAGE) ||
        (!isPercentage && Number(fee) === totalAmountOrPercentage)
      ) {
        callProgramFeeApi();
      } else {
        setShowScheduleTotalAmountWarning({
          isOpen: true,
          isPercentage,
        });
      }
    } else {
      callProgramFeeApi();
    }
  };

  const handleScheduleAmountWarningModalClose = () => {
    setShowScheduleTotalAmountWarning((prev) => ({ ...prev, isOpen: false }));
  };

  const handlePOCurrencyNotMatchWarningModalClose = () => {
    setShowPOCurrencyNotMatchModal(false);
  };

  const handleSalesCreditMinimumModalClose = () => {
    setShowSalesCreditMinimumModal(false);
  };

  const handleSalesCreditPercentageModalClose = () => {
    setShowSalesCreditPercentageModal(false);
  };

  const callProgramFeeApi = () => {
    const { currencyId } = programFeeGetValues();
    const { poNumber } = programGetValues();

    if (poNumber && purchaseOrderData?.currencyTypeId !== currencyId) {
      setShowPOCurrencyNotMatchModal(true);
      return;
    }
    const formValues = programFeeGetValues();
    const [requestPayload, isUpdate] = getProgramFeePayload(formValues);

    if (isUpdate) {
      if (isProgramFeeTabDirty) {
        updateProgramFeeDetailsApi({
          params: requestPayload,
          programId,
        });
      } else {
        handleProgramFeeTabNavigation();
      }
    } else {
      saveProgramFeeDetailsApi({
        params: requestPayload,
        programId,
      });
    }
  };

  const handleBackButtonClick = () => {
    const selectedTabIndex = selectedTab === 0 ? 0 : selectedTab - 1;
    setSelectedTab(selectedTabIndex);
    scrollToTopContainer(500);
  };

  const getTabButtonLabel = (): string => {
    if (isEditProgram) return commonLabels.update;
    if (selectedTab === TAB_INDEX.FEES_AND_EXPENSES) return commonLabels.submit;
    return commonLabels.saveAndNextButtonText;
  };

  const onHandleDeleteTransferApi = () => {
    deleteTransferActivity(programId);
  };

  const handleUpdateConfirmationModalClose = () => {
    setShowUpdateConfirmationModal(false);
  };

  const handleUpdateConfirmationModalPrimaryButtonClick = () => {
    handleProgramFeeRelatedStuff();
    setShowUpdateConfirmationModal(false);
  };
  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };
  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    dispatch(setIsDataModifiedCurrentPage(false));
    dispatch(clearStartChildProgramNavigation()); // Clear Child Program Navigation state on sidebar navigation

    setTimeout(() => {
      navigate(navigationData.navigationPath);
    }, TIMEOUT_100);
    setShowExitConfirmationModal(false);
  };
  return (
    <CreateProgramTabsWrapper>
      <MuiTabs
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[
          tabIdProgram,
          tabIdParticipant,
          tabIdProgramFee,
          tabIdFeesAndExpenses,
          tabIdDocuments,
        ]}
        className="program-candidate-tab"
      >
        <MuiTabPanel
          panelId={TAB_PANELS.PROGRAM}
          value={selectedTab}
          index={TAB_INDEX.PROGRAM}
          isPanelWithMount
          ariaLabelledById={tabIdProgram}
        >
          <FormProvider {...methodsProgram}>
            <ProgramTab
              pageId={pageId}
              clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
              programDetail={programDetail}
              isEditProgram={isEditProgram}
              programId={programId}
              isFromTransfer={isFromTransfer}
              onRefreshProgramDetail={onRefreshProgramDetail}
            />
          </FormProvider>
        </MuiTabPanel>

        <MuiTabPanel
          panelId={TAB_PANELS.PARTICIPANT}
          value={selectedTab}
          index={TAB_INDEX.PARTICIPANT}
          isPanelWithMount
          ariaLabelledById={tabIdParticipant}
        >
          <FormProvider {...methodsParticipant}>
            <ParticipantTab
              pageId={pageId}
              programDetail={programDetail}
              programId={programId}
              isEditProgram={isEditProgram}
              onParentSubmitPressed={onParentSubmitPressed}
            />
          </FormProvider>
        </MuiTabPanel>
        {isEditProgram && (
          <MuiTabPanel
            panelId={TAB_PANELS.DOCUMENTS}
            value={selectedTab}
            index={TAB_INDEX.DOCUMENTS}
            isPanelWithMount
            ariaLabelledById={tabIdDocuments}
          >
            <DocumentsTab pageId={pageId} programId={programId} />
          </MuiTabPanel>
        )}
        <MuiTabPanel
          panelId={TAB_PANELS.PROGRAM_FEE}
          value={selectedTab}
          index={TAB_INDEX.PROGRAM_FEE}
          isPanelWithMount
          ariaLabelledById={tabIdProgramFee}
        >
          <FormProvider {...methodsProgramFee}>
            <ProgramFeeTab
              pageId={pageId}
              programData={programDetail}
              programId={programId}
              isEditProgram={isEditProgram}
              refetchProgramFee={refetchProgramFee}
              purchaseOrderData={purchaseOrderData}
              isRefetch={selectedTab === TAB_INDEX.PROGRAM_FEE}
            />
          </FormProvider>
        </MuiTabPanel>
        {!isFromEditProjectChildPrograms && (
          <MuiTabPanel
            panelId={TAB_PANELS.FEES_AND_EXPENSES}
            value={selectedTab}
            index={TAB_INDEX.FEES_AND_EXPENSES}
            isPanelWithMount
            ariaLabelledById={tabIdFeesAndExpenses}
          >
            <FeesAndExpenseMain
              programId={programId}
              poNumber={programGetValues().poNumber}
              billingCurrency={watchProgramFeeBillingCurrency}
              isRefetch={selectedTab === TAB_INDEX.FEES_AND_EXPENSES}
              isEditProgram={isEditProgram}
              programDetail={programDetail}
            />
          </MuiTabPanel>
        )}
      </MuiTabs>

      <CreateProgramFooter
        pageId={pageId}
        onConfirmClick={() => handleContinueButtonClick(selectedTab + 1)}
        handleNavigateBack={handleBackNavigation}
        isFiledUpdated={
          !isFromEditProjectChildPrograms &&
          (isProgramTabDirty || isParticipantTabDirty)
        }
        confirmButtonText={getTabButtonLabel()}
        showBackButton={selectedTab > 0 && !isEditProgram}
        handleBackButtonClick={handleBackButtonClick}
        cancelMessage={fetchCommonLabel(
          isEditProgram
            ? commonSiteCoreKeys.closeButtonActionText
            : commonSiteCoreKeys.cancelConfirmationText,
        )}
        isFromTransfer={isFromTransfer}
        handleDeleteApi={onHandleDeleteTransferApi}
        cancelButtonText={
          isEditProgram ? commonLabels.close : commonLabels.cancel
        }
        confirmationTitle={
          isEditProgram ? commonLabels.warning : commonLabels.cancelChanges
        }
      />
      {showTransferModal && (
        <CancelFieldModal
          showModal={showTransferModal}
          handleModalClose={() => setShowTransferModal(false)}
          handlePrimaryButtonClick={() => {
            completeParticipantTransfer({
              programId,
            });
          }}
          cancelMessage={getModalLabel(
            programSiteCoreKeys.transferConfirmationnText,
          )}
          title={commonLabels.information}
          primaryButtonTitle={getModalLabel(
            programSiteCoreKeys.yesProceedButton,
          )}
        />
      )}
      {showSuccessModal && (
        <SuccessFieldModal
          handleModalClose={() => {
            setShowSuccessModal(false);
          }}
          showModal={showSuccessModal}
          handleOkButton={handleOkButton}
        />
      )}

      {showScheduleTotalAmountWarning.isOpen && (
        <WarningConfirmationModal
          show={showScheduleTotalAmountWarning.isOpen}
          modalTitle={commonLabels.warning}
          onClose={handleScheduleAmountWarningModalClose}
          onPrimaryButtonClick={handleScheduleAmountWarningModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={
            showScheduleTotalAmountWarning.isPercentage
              ? commonValidationMessages.totalPercentageMustEqual100
              : commonBillingConfigurationDetails.totalAmountMustEqualProgramFeeErrorMessage
          }
        />
      )}

      {showUpdateConfirmationModal && (
        <WarningConfirmationModal
          labelContent={fetchLabel(
            programSiteCoreKeys.programFeeTitle,
            programSiteCoreKeys.areYouSureYouWantToChangeTheProgramFee,
          )}
          modalTitle={commonLabels.confirmation}
          show={showUpdateConfirmationModal}
          onClose={handleUpdateConfirmationModalClose}
          onPrimaryButtonClick={handleUpdateConfirmationModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      {showPOCurrencyNotMatchModal && (
        <WarningConfirmationModal
          show={showPOCurrencyNotMatchModal}
          modalTitle={commonLabels.warning}
          onClose={handlePOCurrencyNotMatchWarningModalClose}
          onPrimaryButtonClick={handlePOCurrencyNotMatchWarningModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            programSiteCoreKeys.programFeeTitle,
            programSiteCoreKeys.programFeeCurrencyHasToBeSameAsPOCurrency,
          )}
        />
      )}
      {showSalesCreditMinimumModal && (
        <WarningConfirmationModal
          show={showSalesCreditMinimumModal}
          modalTitle={commonLabels.warning}
          onClose={handleSalesCreditMinimumModalClose}
          onPrimaryButtonClick={handleSalesCreditMinimumModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            salesCreditSiteCoreKeys.salesCredit,
            salesCreditSiteCoreKeys.minimumOfOneClientRelationshipOwnerMustBeAssignedToSalesCredit,
          )}
        />
      )}
      {showSalesCreditPercentageModal && (
        <WarningConfirmationModal
          show={showSalesCreditPercentageModal}
          modalTitle={commonLabels.warning}
          onClose={handleSalesCreditPercentageModalClose}
          onPrimaryButtonClick={handleSalesCreditPercentageModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            salesCreditSiteCoreKeys.salesCredit,
            salesCreditSiteCoreKeys.totalPercentageOfTheSalesCreditAssignedMustBeEqualTo100,
          )}
        />
      )}
      {showExitConfirmationModal && (
        <WarningConfirmationModal
          labelContent={commonLabels.leavingPageActionText}
          show={showExitConfirmationModal}
          onClose={handleExitConfirmationModalClose}
          onPrimaryButtonClick={handleExitConfirmationModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </CreateProgramTabsWrapper>
  );
};
